import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Icon } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import { RETURN_PARAM, useChangeExit } from '../Reserva/shared'
import type { QueryChanges } from '@/lib/query-params'
import { isServiceError } from '@/services/errors'
import type { Stay } from '@/services/reserva'
import './Pagos.css'

/** Rutas del bloque 3 (inventario D16). */
export const PAY_ROUTES = {
  booking: '/reserva',
  payments: '/reserva/pagos',
  authorize: '/reserva/pagos/autorizar',
  tax: '/reserva/pagos/tasa',
  card: '/reserva/pagos/tarjeta',
  deposit: '/reserva/fianza',
  reserveDeposit: '/reserva/fianza/reservar',
  receipts: '/reserva/recibos',
} as const

export function usePagosStay(): Stay {
  return useLoadedStay()
}

/** "Hotel Mirador del Puerto · Habitación 304 · Doble superior"; sin repetir cuando el PMS da el mismo nombre (D20). */
export function stayName(stay: Stay): string {
  return stay.unit.name && stay.unit.name !== stay.property.name ? `${stay.property.name} · ${stay.unit.name}` : stay.property.name
}

/** Enlace "← Reserva" de las pantallas de la pestaña Reserva (Pagos.dc.html). */
export function BackLink({ to = PAY_ROUTES.booking, label, query }: { to?: string; label?: string; query?: QueryChanges }) {
  const { t } = useTranslation()
  const { go, search } = useAppNavigate()
  return (
    <a
      href={withQuery(to, search, query)}
      className="pagos__back"
      onClick={(e) => {
        e.preventDefault()
        go(to, query ? { query } : {})
      }}
    >
      <Icon name="arrow-left" size={22} />
      {label ?? t('v2.pagos.volverReserva')}
    </a>
  )
}

/** Atrás al origen del flujo (`volver`, como el flujo de cambios); sin origen, Tu reserva (fase 4, B4). */
export function OriginBackLink() {
  const exit = useChangeExit()
  return <BackLink to={exit.path} label={exit.backLabel} query={{ [RETURN_PARAM]: null }} />
}

/** Mensaje de un pago que no ha salido (textos de la app actual, useCardPayment.tsx). */
export function paymentErrorMessage(e: unknown, t: TFunction): string {
  if (!isServiceError(e)) return t('v2.legacy.error.tryAgainOrContactUs')
  if (e.code === 'offline') return t('v2.ds.offline')
  if (e.code === 'already_paid') return t('v2.legacy.payment.error.Payment_already_paid.title')
  if (e.code === 'payment_failed' && e.message === 'card') return t('v2.legacy.payment.error.Payment_failed.description')
  return t('v2.legacy.error.tryAgainOrContactUs')
}

/**
 * Tras un pago fallido la app actual ofrece el pago parcial (`showPartialPayment` en sessionStorage). Se
 * conserva por reserva mientras dure la pestaña.
 */
const PARTIAL_KEY = 'v2.pagos.parcial.'
export function readPartialEnabled(guestCode: string): boolean {
  try {
    return sessionStorage.getItem(PARTIAL_KEY + guestCode) === 'true'
  } catch {
    return false
  }
}
export function enablePartial(guestCode: string): void {
  try {
    sessionStorage.setItem(PARTIAL_KEY + guestCode, 'true')
  } catch {
    /* sin almacenamiento */
  }
}

/** Condiciones de la tarifa: el PMS puede mandar HTML; se muestran como texto (D49, `lib/text.ts`). */
export { plainText } from '@/lib/text'
